/**
 * Match detail view — the brief's largest single design surface (§5.1).
 *
 * v0.1 composition (top-to-bottom):
 *   • Hero matchup (league + state + team logos + score)
 *   • Big AH line w/ freshness pill
 *   • Full synthesis reasoning (named-data depth per D19)
 *   • Persona panel breakdown (one row per persona, with their stance)
 *   • Two primary CTAs (Place bet · Debate)
 *
 * v0.1 explicitly defers: line-movement chart, multi-market alternates,
 * live in-running maintenance string, post-FT recap surface, the
 * Debate chat surface. Each is its own follow-up.
 */

import { useEffect, useState } from 'react';
import type { EdgeCall, Match as MatchT, Persona, UserPick } from '@sharp8/types';
import { falcon } from '../lib/falcon';
import { haptic, tg } from '../lib/telegram';
import { subscribe } from '../lib/eventStream';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';
import { DebateSheet } from '../components/DebateSheet';
import { PlaceBetSheet } from '../components/PlaceBetSheet';
import { SettledRecapCard } from '../components/SettledRecapCard';
import { InPlayStatusString } from '../components/InPlayStatusString';
import { NoEdgeVariant } from '../components/NoEdgeVariant';

interface Props {
  matchId: string;
  onBack(): void;
  /** Where the user is returning to when they tap Back. Drives the
   *  button label so it says "Back to edges" (Home) / "Back to
   *  fixtures" (Matches) / "Back to your picks" (You) instead of
   *  always saying "Back to edges". */
  backTo?: 'home' | 'matches' | 'you';
  /** Optional auto-open intent · 'bet' opens the Place-bet sheet
   *  on mount, 'debate' opens The Debate. Lets Home's CTA buttons
   *  deep-link past the bet-rec card. */
  openSheet?: 'bet' | 'debate';
  /** When a match has multiple +EV edges (AH and OU on the same
   *  fixture) Home tells us which one the user actually tapped, so the
   *  detail surface + Place-bet sheet show that specific call rather
   *  than edges[0]. Falls back to edges[0] if the id isn't present
   *  (e.g. stale edge replaced by SSE between tap and load). */
  edgeId?: string;
  /** Current game bankroll — drives the in-sheet "balance after stake"
   *  preview + insufficient-funds gating on the Place-bet sheet. */
  bankroll?: number;
  /** Notify the parent when a pick is placed so it can refresh the
   *  user's bankroll in App-level state without a /me round-trip. */
  onBankrollChange?(newBankroll: number): void;
}

const PERSONA_AVATARS: Record<string, { bg: string; fg: string; letter: string }> = {
  'sharp8-house': { bg: 'linear-gradient(135deg,#4ED5DB,#2EA8AE)', fg: '#0A1322', letter: 'S' },
  'tanaka':       { bg: 'linear-gradient(135deg,#1A3A6B,#0E1F3D)', fg: '#fff',    letter: 'T' },
  'sofia':        { bg: 'linear-gradient(135deg,#FF6B9D,#C92A6F)', fg: '#fff',    letter: 'S' },
  'arjun':        { bg: 'linear-gradient(135deg,#2D6B47,#1A4A30)', fg: '#fff',    letter: 'A' },
  'caleb':        { bg: 'linear-gradient(135deg,#D4831F,#8B5A0C)', fg: '#fff',    letter: 'C' },
  'lucas':        { bg: 'linear-gradient(135deg,#6B47B8,#422D7A)', fg: '#fff',    letter: 'L' },
};

function capPersona(s: string): string {
  if (s === 'sharp8-house') return 'Sharp8 House';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} UTC`;
}

export function Match({ matchId, onBack, backTo = 'home', openSheet, edgeId, bankroll, onBankrollChange }: Props) {
  const backLabel =
    backTo === 'matches' ? '← Back to fixtures' :
    backTo === 'you'     ? '← Back to your picks' :
                           '← Back to edges';
  const [match, setMatch] = useState<MatchT | null>(null);
  const [edges, setEdges] = useState<EdgeCall[] | null>(null);
  const [personas, setPersonas] = useState<Persona[] | null>(null);
  // Auto-open intent from a deep-link CTA on Home — opens the matching
  // sheet as soon as the match data lands. Initialized from the prop
  // so refreshes / back-then-forward don't re-fire.
  const [debateOpen, setDebateOpen] = useState(openSheet === 'debate');
  const [betOpen, setBetOpen] = useState(openSheet === 'bet');
  // Picks the current user has already placed on THIS match. If non-
  // empty, swap the "Place bet" CTA for an "Already placed" status
  // block — prevents accidental double-betting on the same edge call.
  const [myPicks, setMyPicks] = useState<UserPick[] | null>(null);

  // Live updates · match + edges via SSE.
  //   · Initial one-shot fetch seeds the screen + pulls personas
  //     (which the stream doesn't include — stable per-app metadata).
  //   · /streams/match/:id pushes a 'snapshot' event whenever the
  //     match row or its edges change in DB. Drives live score,
  //     EDGE freshness string, and the FT→SettledRecapCard handoff.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      falcon.match.get(matchId),
      falcon.edges.forMatch(matchId),
      falcon.personas.list(),
    ])
      .then(([m, e, p]) => { if (!cancelled) { setMatch(m); setEdges(e); setPersonas(p); } })
      .catch((err) => console.warn('[match] load failed:', err));

    const handle = subscribe<{ match: MatchT | null; edges: EdgeCall[] }>({
      path: `streams/match/${encodeURIComponent(matchId)}`,
      events: ['snapshot'],
      onEvent: (payload) => {
        if (cancelled) return;
        if (payload.match) setMatch(payload.match);
        setEdges(payload.edges);
      },
      onError: () => {
        // Soft fallback — re-fetch via the SDK so the user keeps seeing
        // fresh data while EventSource auto-reconnects in the background.
        falcon.match.get(matchId).then((m) => { if (!cancelled && m) setMatch(m); }).catch(() => {});
        falcon.edges.forMatch(matchId).then((e) => { if (!cancelled) setEdges(e); }).catch(() => {});
      },
    });
    return () => { cancelled = true; handle.close(); };
  }, [matchId]);

  // Have-I-bet-on-this-match? · SSE-driven so settlement
  // (pending → won/lost/pushed + payout) flips the AlreadyPlaced row
  // status the moment the cron's settle job runs. Filtered server-side
  // via ?matchId — only this match's picks ride the stream.
  useEffect(() => {
    let cancelled = false;
    const initData = tg()?.initData ?? '';
    if (!initData) { setMyPicks([]); return; }

    const refetch = () => {
      fetch(`${API_BASE.replace(/\/$/, '')}/me/picks?matchId=${encodeURIComponent(matchId)}`, {
        headers: { 'X-Telegram-Init-Data': initData },
      })
        .then((r) => r.ok ? r.json() : { picks: [] })
        .then((j: { picks: UserPick[] }) => { if (!cancelled) setMyPicks(j.picks ?? []); })
        .catch(() => { /* keep last-good myPicks rather than blanking */ });
    };
    refetch();  // initial seed
    const handle = subscribe<{ picks: UserPick[]; bankroll?: number }>({
      path: 'streams/me/picks',
      events: ['picks'],
      withInitData: true,
      query: { matchId },
      onEvent: (payload) => {
        if (cancelled) return;
        setMyPicks(payload.picks);
        // Settle credits land here too — keep the Home BankrollPill
        // accurate while the user lingers on a match detail.
        if (typeof payload.bankroll === 'number') {
          onBankrollChange?.(payload.bankroll);
        }
      },
      onError: () => { refetch(); },
    });
    return () => { cancelled = true; handle.close(); };
  }, [matchId, betOpen]);  // betOpen in deps so closing the sheet after a place re-fetches

  if (!match || !edges || !personas) {
    return (
      <div style={{ padding: '80px 32px', textAlign: 'center', color: 'var(--ink-mute)' }}>
        Loading match…
      </div>
    );
  }

  // Prefer the edge the user tapped on Home; otherwise fall back to the
  // first (highest-EV) edge. Without this disambiguation, tapping the AH
  // hero on Home could land on the OU edge on the detail surface.
  const edge = (edgeId && edges.find((e) => e.id === edgeId)) || edges[0];
  const isLive = match.state === 'live';
  const isSettled = match.state === 'ft' || match.state === 'settled';
  // Betting is locked the moment the match starts. Covers two cases:
  //   · state='live' — clearly in-play, line is stale
  //   · state='pre' but kickoff is already in the past — transition
  //     gap (live-refresh hasn't promoted the row yet, typically <2 min)
  const kickoffPast = !!match.kickoff && new Date(match.kickoff).getTime() <= Date.now();
  const bettingLocked = isLive || (match.state === 'pre' && kickoffPast);

  // Per Match spec §5 (Q32-folded): when the match has reached FT,
  // replace the active-call + CTAs surface with the recap card.
  if (isSettled) {
    return (
      <div className="match">
        <SettledRecapCard match={match} edge={edge ?? null} personas={personas} />
        <button className="match__back" onClick={onBack}>{backLabel}</button>

        {/* The Debate stays available in own-the-verdict mode (§4.3) */}
        <DebateSheet
          matchId={matchId}
          matchLabel={`${match.home.shortName} vs ${match.away.shortName}`}
          open={debateOpen}
          onClose={() => setDebateOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="match">
      {/* HERO MATCHUP */}
      <section className="match__hero">
        <div className="match__league">
          <span>{match.league.name}</span>
          {isLive && match.score && (
            <span className="match__live">LIVE · {match.score.minute}</span>
          )}
        </div>
        <div className="match__teams">
          <div className="match__team">
            <span className="match__crest" style={{ background: 'var(--surface-tint)' }}>
              <img
                src={match.home.crestUrl}
                alt={match.home.name}
                onError={(e) => {
                  const img = e.currentTarget;
                  const parent = img.parentElement;
                  if (parent) {
                    parent.style.background = match.home.primaryColor;
                    parent.innerHTML = `<span style="color:#fff;font-weight:800;font-size:18px">${match.home.shortName[0]}</span>`;
                  }
                }}
              />
            </span>
            <div className="match__team-name">{match.home.shortName}</div>
          </div>

          {isLive && match.score ? (
            <div className="match__score">
              <div className="match__score-nums">{match.score.home} – {match.score.away}</div>
              <div className="match__score-min">{match.score.minute}</div>
            </div>
          ) : (
            <div className="match__vs">VS</div>
          )}

          <div className="match__team">
            <span className="match__crest" style={{ background: 'var(--surface-tint)' }}>
              <img
                src={match.away.crestUrl}
                alt={match.away.name}
                onError={(e) => {
                  const img = e.currentTarget;
                  const parent = img.parentElement;
                  if (parent) {
                    parent.style.background = match.away.primaryColor;
                    parent.innerHTML = `<span style="color:#fff;font-weight:800;font-size:18px">${match.away.shortName[0]}</span>`;
                  }
                }}
              />
            </span>
            <div className="match__team-name">{match.away.shortName}</div>
          </div>
        </div>
      </section>

      {edge ? (
        <>
          {/* IN-PLAY STATUS STRING · Match spec §3.1
              Above the bet-rec card during Live state only. The card
              itself is frozen at the Pre-match call; this string
              communicates the current freshness state. */}
          {isLive && (
            <section className="match__inplay">
              <InPlayStatusString edge={edge} />
            </section>
          )}

          {/* THE CALL */}
          <section className="match__call">
            <div className="match__call-label">Edge call</div>
            <div className="match__call-line">
              <span className="line">Take {edge.line}</span>
              <span className="at">@</span>
              <span className="price">{edge.price.toFixed(2)}</span>
            </div>
            <div className="match__call-book">· {edge.book}</div>
            <div className="match__call-freshness">
              <span className="dot" />
              {edge.freshness.toUpperCase()}
              <span className="delta">+{(edge.ev * 100).toFixed(0)}% EV · verified {fmtTime(edge.verifiedAt)}</span>
            </div>
          </section>

          {/* SYNTHESIS */}
          <section className="match__synth">
            <div className="match__section-label">Synthesis</div>
            <p className="match__synth-body">{edge.reasoning.body}</p>
            {edge.reasoning.citations.length > 0 && (
              <div className="match__cites">
                {edge.reasoning.citations.map((c, i) =>
                  c.url ? (
                    <a key={i} className="match__cite" href={c.url} target="_blank" rel="noopener noreferrer">
                      {c.source} · {fmtTime(c.verifiedAt)}
                    </a>
                  ) : (
                    <span key={i} className="match__cite match__cite--inert">
                      {c.source} · {fmtTime(c.verifiedAt)}
                    </span>
                  )
                )}
              </div>
            )}
          </section>

          {/* PANEL BREAKDOWN */}
          <section className="match__panel">
            <div className="match__section-label">
              The panel · {edge.panel.backing} of {edge.panel.total} back this call
            </div>
            <div className="match__panel-rows">
              {personas.map((p) => {
                const isDissent = edge.panel.dissent.some((d) => d.personaKey === p.key);
                const av = PERSONA_AVATARS[p.key];
                if (!av) return null;
                return (
                  <div key={p.key} className={`match__panel-row${isDissent ? ' is-dissent' : ''}`}>
                    <span className="match__panel-av" style={{ background: av.bg, color: av.fg }}>
                      {av.letter}
                    </span>
                    <div className="match__panel-meta">
                      <div className="match__panel-name">{p.name}</div>
                      <div className="match__panel-tag">{p.tagline}</div>
                    </div>
                    <span className={`match__panel-stance${isDissent ? ' is-dissent' : ' is-backing'}`}>
                      {isDissent ? 'DISSENT' : 'BACK'}
                    </span>
                  </div>
                );
              })}
            </div>
            {edge.panel.dissent[0] && (
              <div className="match__dissent-detail">
                <b>{capPersona(edge.panel.dissent[0].personaKey)} dissents:</b>{' '}
                {edge.panel.dissent[0].reasoning}{' '}
                <i>Their line: {edge.panel.dissent[0].alternativeLine}.</i>
              </div>
            )}
          </section>

          {/* CTAs · gated on match state. Once kickoff passes the line
              is locked — show the Debate CTA but not Place bet, since
              betting a stale pre-match price after kickoff would be
              dishonest UX. The cron's settle job will mark the pick
              outcome once state flips to 'ft'. */}
          {myPicks && myPicks.length > 0 ? (
            <AlreadyPlaced picks={myPicks} onOpenDebate={() => { haptic('light'); setDebateOpen(true); }} />
          ) : bettingLocked ? (
            <LockedCta
              isLive={isLive}
              onOpenDebate={() => { haptic('light'); setDebateOpen(true); }}
            />
          ) : (
            <section className="match__ctas">
              <button className="match__cta match__cta--primary" onClick={() => { haptic('medium'); setBetOpen(true); }}>
                Place bet · {edge.price.toFixed(2)}
              </button>
              <button className="match__cta match__cta--secondary" onClick={() => { haptic('light'); setDebateOpen(true); }}>
                Debate
              </button>
            </section>
          )}
        </>
      ) : (
        <NoEdgeVariant match={match} />
      )}

      {/* Footer · back action (in case Telegram BackButton is unavailable) */}
      <button className="match__back" onClick={onBack}>{backLabel}</button>

      {/* The Debate · per-match chat sheet */}
      <DebateSheet
        matchId={matchId}
        matchLabel={`${match.home.shortName} vs ${match.away.shortName}`}
        open={debateOpen}
        onClose={() => setDebateOpen(false)}
      />

      {/* Place-bet · stake selector sheet */}
      {edge && (
        <PlaceBetSheet
          edge={edge}
          matchLabel={`${match.home.shortName} vs ${match.away.shortName}`}
          open={betOpen}
          onClose={() => setBetOpen(false)}
          bankroll={bankroll}
          existingPicks={myPicks ?? undefined}
          onPlaced={(_pick, newBankroll) => onBankrollChange?.(newBankroll)}
        />
      )}
    </div>
  );
}

// ── Locked CTA — match has kicked off, pre-match price no longer
//    bettable. Renders in place of Place bet + Debate.

function LockedCta({ isLive, onOpenDebate }: { isLive: boolean; onOpenDebate: () => void }) {
  return (
    <section className="match__locked">
      <div className="match__locked-head">
        <span className="match__locked-tag">{isLive ? 'IN PLAY' : 'LOCKED'}</span>
        <span className="match__locked-msg">
          {isLive
            ? "Match in progress · pre-match price locked, settles at FT."
            : "Kickoff has passed · price locked, awaiting live state."}
        </span>
      </div>
      <button
        className="match__cta match__cta--secondary match__locked-debate"
        onClick={onOpenDebate}
      >
        Debate
      </button>
    </section>
  );
}

// ── Already-placed CTA replacement ─────────────────────────────────
//
// When the user has already placed at least one pick on this match,
// the Place-bet CTA is replaced by a status summary so they don't
// accidentally double-bet on the same edge call. Debate stays
// available — questions about a pick you've placed are exactly when
// you'd want it.

function AlreadyPlaced({ picks, onOpenDebate }: { picks: UserPick[]; onOpenDebate: () => void }) {
  return (
    <section className="match__placed">
      <div className="match__placed-head">
        <span className="match__placed-tag">YOU'VE PLACED</span>
        <span className="match__placed-count">
          {picks.length === 1 ? '1 pick' : `${picks.length} picks`}
        </span>
      </div>
      <ul className="match__placed-list">
        {picks.map((p) => {
          const statusCls =
            p.status === 'won'    ? 'won'    :
            p.status === 'lost'   ? 'lost'   :
            p.status === 'pushed' ? 'pushed' :
                                    'pending';
          // Half-line outcomes recovered from stored payout:
          //   half-WIN  · won but payout < stake*price
          //   half-LOSS · lost but payout > 0 (the pushed half got refunded)
          const fullWinPayout = p.stake * p.price;
          const isHalfWin  = p.status === 'won'  && p.payout !== null && p.payout < fullWinPayout - 0.01;
          const isHalfLoss = p.status === 'lost' && p.payout !== null && p.payout > 0.01;
          let resultText: string;
          if (p.status === 'won' && p.payout !== null) {
            const profit = p.payout - p.stake;
            resultText = `${profit >= 0 ? '+' : '−'}$${Math.abs(profit).toFixed(2)}${isHalfWin ? ' (½)' : ''}`;
          } else if (p.status === 'lost') {
            const loss = p.stake - (p.payout ?? 0);
            resultText = `−$${loss.toFixed(2)}${isHalfLoss ? ' (½)' : ''}`;
          } else if (p.status === 'pushed') {
            resultText = '$0.00 (push)';
          } else {
            resultText = 'pending';
          }
          return (
            <li key={p.id} className="match__placed-row">
              <div className="match__placed-row-line">
                <span className="match__placed-line">{p.line}</span>
                <span className="match__placed-meta"> @ {p.price.toFixed(2)} · ${p.stake.toFixed(2)}</span>
              </div>
              <span className={`match__placed-status match__placed-status--${statusCls}`}>{resultText}</span>
            </li>
          );
        })}
      </ul>
      <button className="match__cta match__cta--secondary match__placed-debate" onClick={onOpenDebate}>
        Open Debate
      </button>
    </section>
  );
}
